import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Nondeterministic turing machine simulator in 23 lines of JavaScript",
  "description": "",
  "published": "2011-11-23T00:00:00.000Z",
  "redirect_from": ["/blog/nondeterministic-turing-machine-simulator-in-23-lines-of-javascript/swizec/3047"],
  "categories": "Alan Turing, Algorithm, Datastructures, Kurt Gödel, Turing machine, Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2011/11/Screen-Shot-2011-11-23-at-10.10.32-PM.png",
        "alt": "The machine I used for testing",
        "title": "The machine I used for testing"
      }}></img></p>
    <p>{`Felt like doing some coding last night and since my friends were just trolling me about codegolfing a `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Turing_machine",
        "title": "Turing machine"
      }}>{`turing machine simulator`}</a>{`(they need it for a term paper), I decided to do just that.`}</p>
    <p>{`At first I wanted to make it super simple, 10 `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Source_lines_of_code",
        "title": "Source lines of code"
      }}>{`lines of code`}</a>{` max ... and I failed.`}</p>
    <p>{`What better way to embrace failure than to implement a simulator of `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Non-deterministic_Turing_machine",
        "title": "Non-deterministic Turing machine"
      }}>{`nondeterministic turing machines`}</a>{` in as little lines of code as possible?`}</p>
    <p>{`Adding "eps" to the alphabet of course - basically a means for the machine to know when it's gone off either end of the tape and that it can also write nothing to the tape. Both very useful features!`}</p>
    <p>{`And here's the code in all its glory. You can also view it on `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/random-coding/blob/master/turing.js"
      }}>{`github`}</a>{` if that's your thing.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// transition format: {from: 'q1', to: 'q2', via: 'A', put: 'B', move: 1/-1}
// spits out whether end state was reached
// basic way to run:  node turing.js machine.json 101 q4
// node turing.js

var _ = require("underscore"),
  states = {};

var delta = function (states, step, end) {
  if (_.keys(step).indexOf(end) >= 0) return true;
  var _step = {},
    foo = _.keys(step).map(function (k) {
      var i = step[k][0],
        t = step[k][1],
        cur = i < 0 || i >= t.length ? states[k]["esp"] : states[k][t[i]];
      if (cur)
        cur.map(function (cur) {
          if (cur.put != "eps") t.splice(i, 1, cur.put);
          _step[cur.to] = [i + cur.move, t];
        });
    });
  return _.size(_step) ? delta(states, _step, end) : false;
};

JSON.parse(require("fs").readFileSync(process.argv[2], "utf-8")).map(function (
  i
) {
  states[i.from] = states[i.from] || {};
  states[i.from][i.via] = states[i.from][i.via] || [];
  states[i.from][i.via].push(i);
});

console.log(
  delta(states, { q0: [0, process.argv[3].split("")] }, process.argv[4])
);
`}</code></pre>
    <p>{`Basically what you're doing is performing a `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Breadth-first_search",
        "title": "Breadth-first search"
      }}>{`breadth-first-search`}</a>{` on a graph, looking for the end state and a pinch of complications with observing contents the tape as well. You should make sure not to accidentally implement a `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Depth-first_search",
        "title": "Depth-first search"
      }}>{`depth-first search`}</a>{` since some branches might never terminate.`}</p>
    <p>{`The above implementation could probably be codegolfed some more, but I don't like codegolfing by just compating lines beyond what is reasonable. I much prefer doing it with languages features and perhaps algorithmic improvements.`}</p>
    <p>{`One of the biggest hindrances to making this much shorter is that a lot of operations on javascript arrays affect state, but don't return the new object as well. Lost at least three lines because of this!`}</p>
    <p>{`An approach worth trying would be to perform a reduce on the tree of states and if the start and end state ever overlap, you know to return a truthy value, otherwise you go with something falsy. But I wasn't sure how to go about that.`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://xlinux.nist.gov/dads//"
        }}>{`Dictionary of Algorithms and Datastructures`}</a>{` (xlinux.nist.gov)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://compassioninpolitics.wordpress.com/2011/09/29/free-will-quantum-theory-and-brain-science/"
        }}>{`Free Will, Quantum Mechanics, and Brain Science: John Polkinghorne Quotes`}</a>{` (compassioninpolitics.wordpress.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://rjlipton.wordpress.com/2011/07/28/logic-in-action/"
        }}>{`Logic In Action`}</a>{` (rjlipton.wordpress.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://rjlipton.wordpress.com/2011/07/04/getting-on-base-with-pnp/"
        }}>{`Getting On Base With P=NP`}</a>{` (rjlipton.wordpress.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://rjlipton.wordpress.com/2011/10/31/an-interview-with-kurt-gdel/"
        }}>{`An Interview With Kurt Gödel`}</a>{` (rjlipton.wordpress.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://work.damow.net/random/bf-turing/"
        }}>{`The Brainfu`}{`*`}{`k Turing Machine (Javascript)`}</a>{` (work.damow.net)`}</li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=c89197ff-e3d0-4791-867f-053288550625",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      